import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Heart, Radio, Send } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useSiteConfig } from "../context/ConfigContext";
import ProductCard from "../components/ProductCard";

export default function LiveDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const { features } = useSiteConfig();
  const navigate = useNavigate();
  const [stream, setStream] = useState(null);
  const [error, setError] = useState("");
  const [chat, setChat] = useState([]);
  const [text, setText] = useState("");
  const [liked, setLiked] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api(`/live/${id}`)
      .then(setStream)
      .catch((e) => setError(e.message));
    const load = () => api(`/live/${id}/chat`).then((c) => setChat(Array.isArray(c) ? c : c.messages || [])).catch(() => {});
    load();
    const tm = setInterval(load, 5000);
    return () => clearInterval(tm);
  }, [id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat.length]);

  const like = async () => {
    if (liked) return;
    setLiked(true);
    try {
      await api(`/live/${id}/like`, { method: "POST" });
      setStream((s) => (s ? { ...s, likes: (s.likes || 0) + 1 } : s));
    } catch {}
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!user) {
      navigate(`/login?next=/live/${id}`);
      return;
    }
    const body = { text: text.trim(), user_name: user.name };
    setText("");
    try {
      await api(`/live/${id}/chat`, { method: "POST", auth: true, body });
      const c = await api(`/live/${id}/chat`);
      setChat(Array.isArray(c) ? c : c.messages || []);
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (features.live === false)
    return (
      <div data-testid="live-off" className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="font-heading text-xl font-semibold text-slate-800">Live shopping</p>
        <p className="mt-2 text-sm text-slate-500">{t("no_live")}</p>
      </div>
    );

  if (error)
    return (
      <div data-testid="live-error" className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="font-heading text-xl font-semibold text-slate-800">{t("empty_t")}</p>
        <Link to="/live" className="mt-6 inline-block rounded-full bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white">
          {t("live_t")}
        </Link>
      </div>
    );

  if (!stream)
    return (
      <div data-testid="live-loading" className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-6 animate-pulse">
          <div className="lg:col-span-2 aspect-video rounded-3xl bg-slate-100" />
          <div className="aspect-video lg:aspect-auto rounded-3xl bg-slate-100" />
        </div>
      </div>
    );

  return (
    <div data-testid="live-detail-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="relative overflow-hidden rounded-3xl bg-teal-950 aspect-video">
            {stream.video_url ? (
              <video data-testid="live-video" src={mediaUrl(stream.video_url)} controls autoPlay muted className="h-full w-full object-cover" />
            ) : (
              <>
                {stream.thumbnail && <img src={mediaUrl(stream.thumbnail)} alt={stream.title} className="h-full w-full object-cover opacity-60" />}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <Radio className="h-10 w-10 text-teal-400 animate-pulse" />
                  <p className="mt-3 font-heading text-lg font-semibold text-white">{t("streaming_note")}</p>
                  <p className="mt-1 text-sm text-teal-100/70">{t("streaming_sub")}</p>
                </div>
              </>
            )}
            {stream.status === "live" && (
              <span className="absolute top-4 left-4 flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Live
              </span>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 truncate">{stream.title}</h1>
              <p className="text-sm text-slate-600">{t("hosted_by")} {stream.host_name || stream.supplier_name}</p>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <span data-testid="live-viewers" className="flex items-center gap-1.5"><Eye className="h-4 w-4" /> {stream.viewers ?? 0}</span>
              <button
                data-testid="live-like-button"
                onClick={like}
                className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-semibold transition active:scale-95 ${
                  liked ? "border-red-300 bg-red-50 text-red-600" : "border-slate-300 text-slate-600 hover:border-red-300 hover:text-red-600"
                }`}
              >
                <Heart className={`h-4 w-4 ${liked ? "fill-red-500 text-red-500" : ""}`} /> {stream.likes ?? 0}
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="font-heading font-semibold text-slate-900">{t("live_chat")}</h2>
          </div>
          <div data-testid="live-chat-messages" className="flex-1 min-h-[240px] max-h-[380px] overflow-y-auto px-4 py-3 space-y-2.5 bg-[#F8FAF7]">
            {chat.length === 0 && <p className="text-xs text-slate-400 text-center pt-6">{t("no_msgs")}</p>}
            {chat.map((m, i) => (
              <div key={m.id || i} className="text-sm">
                <span className="font-semibold text-teal-800">{m.user_name || m.sender || "Guest"}: </span>
                <span className="text-slate-700">{m.text}</span>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="flex items-center gap-2 border-t border-slate-200 px-3 py-3">
            <input
              data-testid="live-chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={300}
              placeholder={user ? t("chat_ph") : t("login_chat")}
              className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
            <button
              data-testid="live-chat-send"
              type="submit"
              disabled={!text.trim()}
              className="rounded-full bg-teal-700 p-2.5 text-white hover:bg-teal-800 transition active:scale-95 disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>

      {stream.products?.length > 0 && (
        <section className="mt-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">{t("featured_live")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {stream.products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
